import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useClerk } from "@clerk/react";
import { ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useMe } from "@/context/MeContext";
import { Wordmark } from "@/components/Wordmark";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function AgeGate() {
  const { refresh } = useMe();
  const { signOut } = useClerk();
  const [dob, setDob] = useState("");
  const [min, setMin] = useState(16);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/privacy/me").then((r) => setMin(r.data.min_age)).catch(() => {}); }, []);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/privacy/age", { date_of_birth: dob });
      if (data.blocked) { await signOut({ redirectUrl: `/age-restricted?min=${data.min_age}` }); return; }
      await refresh();
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  return (
    <div className="mx-auto max-w-md space-y-6 fade-up" data-testid="age-gate">
      <Wordmark />
      <h1 className="text-4xl font-medium tracking-tight">When were you born?</h1>
      <p className="text-slate-600">Middle is for people aged {min} and over. We ask once, when you join, so the right privacy protections apply. If you're under 18, safer settings are switched on for you.</p>
      <div className="surface space-y-3 p-5">
        <label htmlFor="dob" className="text-sm font-medium">Date of birth</label>
        <Input id="dob" type="date" data-testid="age-gate-dob" value={dob} max={new Date().toISOString().slice(0, 10)} onChange={(e) => setDob(e.target.value)} />
        <Button data-testid="age-gate-submit" className="w-full rounded-full" disabled={!dob || busy} onClick={submit}>{busy ? "Checking…" : "Continue"}</Button>
      </div>
      <p className="flex gap-2 text-xs text-slate-500"><ShieldCheck size={15} className="shrink-0 text-[hsl(var(--facts))]" />Your date of birth is never shown on your profile. We don't ask for ID. You can ask for a correction later from the Privacy Centre.</p>
    </div>
  );
}

export const AgeRestricted = () => {
  const min = new URLSearchParams(useLocation().search).get("min") || "16";
  return (
    <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center space-y-5 px-6" data-testid="age-restricted">
      <Wordmark />
      <h1 className="text-4xl font-medium tracking-tight">Middle isn't available for you yet</h1>
      <p className="text-slate-600">Middle is for people aged {min} and over. We've deleted the account and the details you gave us.</p>
      <Link to="/" className="text-sm font-medium underline" data-testid="age-restricted-home">Back to Middle</Link>
    </div>
  );
};
